import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import { routeTree } from "./routeTree.gen"

import { ApiError } from "./client"
import { CustomProvider, PageErrorBoundary } from "./components/infrastructure"
import { AuthProvider } from "./components/providers/AuthProvider"
import { startWebVitalsMonitoring } from "./monitoring/webVitals"
import { setAuthRedirectHandler, setCacheClearHandler } from "./services/api"
import { useAuthStore } from "./stores/auth"
import { clearDynamicImportReloadFlag } from "./utils/lazyWithRetry"

const handleApiError = (error: Error) => {
  if (error instanceof ApiError && [401, 403].includes(error.status)) {
    const { clearAuth } = useAuthStore.getState()
    clearAuth()
  }
}

/**
 * Custom retry function that prevents retrying on authentication errors (401/403).
 * Without this, TanStack Query's default retry (3 attempts) can cause rapid-fire
 * requests when credentials are invalid, flooding the console with errors.
 */
const shouldRetryQuery = (failureCount: number, error: Error): boolean => {
  // Never retry auth errors — they won't succeed without re-authentication
  if (error instanceof ApiError && [401, 403].includes(error.status)) {
    return false
  }
  // For other errors, allow up to 3 retries (TanStack Query default)
  return failureCount < 3
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  mutationCache: new MutationCache({
    onError: handleApiError,
  }),
})

setCacheClearHandler(() => {
  queryClient.clear()
})

const router = createRouter({ routeTree })
setAuthRedirectHandler((path = "/login") => {
  router.navigate({ to: path })
})
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

startWebVitalsMonitoring()

// Clear reload flags on successful startup.
// This prevents infinite reload loops if a chunk fails persistently.
clearDynamicImportReloadFlag()
sessionStorage.removeItem("chunk-error-reload")

// Render the app
ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PageErrorBoundary>
      <CustomProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </CustomProvider>
    </PageErrorBoundary>
  </StrictMode>,
)
